import { ErrorMessage, Field, Formik } from "formik";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import {
    Alert,
    Button, Col, Form, Image, Modal, Row, Spinner,
    Stack
} from "react-bootstrap";
import * as Yup from "yup";
import { IChecklistDefinitionDetail } from "../../../../customTypings/task/ChecklistDefinition";
import { IChecklistDefinitionFull } from "../../../../customTypings/task/ChecklistDefinitionFull";
import { IChecklistDefinitionItem } from "../../../../customTypings/task/ChecklistDefinitionItem";
import { checklistDefinitionService } from "../../../../services";
import { EditChecklistDefitionItems } from "../EditChecklistDefinitionItems";
import { LoadingSpinner } from "../../../common/Loading/LoadingSpinner";
import { showToastSuccess, showToastError } from "../../../../utils";

dayjs.extend(advancedFormat);
dayjs.extend(utc);

function formatMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

type Props = {
    show: boolean;
    onClose: (updated: boolean) => void;
    checklistDefinitionId: string;
};

const EditChecklist: React.FC<Props> = ({
    show,
    onClose,
    checklistDefinitionId,
}) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [currentChecklistDefinition, setCurrentChecklistDefinition] =
        useState<IChecklistDefinitionDetail>();
    const [
        currentChecklistDefinitionItems,
        setCurrentChecklistDefinitionItems,
    ] = useState<IChecklistDefinitionItem[]>();
    const [checklistDefinitionItems, setChecklistDefinitionItems] =
        useState<IChecklistDefinitionItem[]>();
    const [loading, setLoading] = useState(true);

    const handleClose = () => {
        setChecklistDefinitionItems(currentChecklistDefinitionItems);
        onClose(false);
    };

    useEffect(() => {
        setLoading(true);
        checklistDefinitionService
            .getDetail(checklistDefinitionId)
            .then((checklistDefinition) => {
                setCurrentChecklistDefinition(checklistDefinition);

                checklistDefinitionService
                    .getItems(checklistDefinitionId)
                    .then(
                        (
                            checklistDefinitionitems: IChecklistDefinitionItem[]
                        ) => {
                            setCurrentChecklistDefinitionItems(
                                checklistDefinitionitems
                            );
                            setChecklistDefinitionItems(
                                checklistDefinitionitems
                            );
                            setLoading(false);
                        }
                    );
            });
    }, [checklistDefinitionId]);

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose}>
            {loading || !currentChecklistDefinition ? (
                <Stack className="justify-content-center mb-5" gap={3}>
                    <Image
                        src="/froom-waste-tracker-logo.svg"
                        className="mx-auto"
                        style={{ height: "5rem" }}
                    />
                    <Spinner
                        animation={"border"}
                        variant="info"
                        className="mx-auto"
                    />
                </Stack>
            ) : (
                <Formik
                    initialValues={{
                        name: currentChecklistDefinition.name,
                        type: currentChecklistDefinition.type,
                    }}
                    validationSchema={Yup.object().shape({
                        name: Yup.string().required("Name is required"),
                    })}
                    onSubmit={(
                        values,
                        { setStatus, setSubmitting, setFieldError }
                    ) => {
                        setStatus();
                        setSubmitting(false);
                        const updatedChecklist: IChecklistDefinitionFull = {
                            id: currentChecklistDefinition.id,
                            name: values.name,
                            type: values.type,
                            isDeleted: currentChecklistDefinition.isDeleted,
                            items: checklistDefinitionItems ?? [],
                        };
                        checklistDefinitionService
                            .editFull(updatedChecklist)
                            .then(
                                (response) => {
                                    setSubmitting(false);
                                    if (response.status !== "Failure") {
                                        setCurrentChecklistDefinitionItems(
                                            checklistDefinitionItems
                                        );
                                        checklistDefinitionService
                                            .getDetail(checklistDefinitionId)
                                            .then((d) => setCurrentChecklistDefinition(d))
                                            .catch(() => {});
                                        showToastSuccess("Checklist updated successfully!");
                                        setTimeout(() => onClose(true), 1000);
                                    } else {
                                        setAlertVariant("danger");
                                        showToastError(response.message);
                                    }
                                },
                                (error: any) => {
                                    setAlertVariant("danger");
                                    if (error.status === 400 && error.errors) {
                                        Object.keys(error.errors).forEach((key) => {
                                            const messages = error.errors[key];
                                            if (Array.isArray(messages)) {
                                                messages.forEach((message) => showToastError(message));
                                            }
                                        });
                                    } else {
                                        showToastError("Something went wrong. Please try again.");
                                    }
                                    setSubmitting(false);
                                }
                            );
                    }}
                >
                    {({
                        errors,
                        status,
                        touched,
                        isSubmitting,
                        handleSubmit,
                        values,
                        handleChange,
                        setFieldValue,
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Modal.Header closeButton>
                                <Modal.Title>Update Checklist</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="d-flex flex-wrap align-items-start gap-3 gap-md-4 mb-3 small">
                                    {currentChecklistDefinition.createdAtUtc && (
                                        <div className="d-flex text-start align-items-start">
                                            <span className="text-muted text-nowrap me-2">Created:</span>
                                            <div>
                                                <div>{formatMetaDate(currentChecklistDefinition.createdAtUtc)}</div>
                                                <div className="text-muted">
                                                    by {formatByLine(currentChecklistDefinition.createdByDisplayName)}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {currentChecklistDefinition.updatedAtUtc && (
                                        <div className="d-flex text-start align-items-start">
                                            <span className="text-muted text-nowrap me-2">Last updated:</span>
                                            <div>
                                                <div>{formatMetaDate(currentChecklistDefinition.updatedAtUtc)}</div>
                                                <div className="text-muted">
                                                    by {formatByLine(currentChecklistDefinition.lastUpdatedByDisplayName)}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {currentChecklistDefinition.isDeleted &&
                                        (currentChecklistDefinition.deletedAuditAtUtc ||
                                            currentChecklistDefinition.updatedAtUtc) && (
                                        <div className="d-flex text-start align-items-start ms-md-auto">
                                            <span className="text-danger text-nowrap me-2">Deleted:</span>
                                            <div>
                                                <div>
                                                    {formatMetaDate(
                                                        currentChecklistDefinition.deletedAuditAtUtc ??
                                                            currentChecklistDefinition.latestAuditActionAtUtc ??
                                                            currentChecklistDefinition.updatedAtUtc
                                                    )}
                                                </div>
                                                <div className="text-muted">
                                                    by {formatByLine(currentChecklistDefinition.deletedByDisplayName)}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Form.Group
                                    as={Row}
                                    className="mb-3"
                                    controlId="name"
                                >
                                    <Form.Label column sm={3}>
                                        Name
                                    </Form.Label>
                                    <Col sm={9}>
                                        <Field
                                            name="name"
                                            type="text"
                                            className={
                                                "form-control" +
                                                (errors.name && touched.name
                                                    ? " is-invalid"
                                                    : "")
                                            }
                                        />
                                        <ErrorMessage
                                            name="name"
                                            component="div"
                                            className="invalid-feedback"
                                        />
                                    </Col>
                                </Form.Group>
                                <Form.Group
                                    as={Row}
                                    className="mb-3"
                                    controlId="type"
                                >
                                    <Form.Label column sm={3}>
                                        Type
                                    </Form.Label>
                                    <Col sm={9}>
                                        <Field
                                            name="type"
                                            type="text"
                                            className={
                                                "form-control" +
                                                (errors.type && touched.type
                                                    ? " is-invalid"
                                                    : "")
                                            }
                                        />
                                        <ErrorMessage
                                            name="type"
                                            component="div"
                                            className="invalid-feedback"
                                        />
                                    </Col>
                                </Form.Group>
                                {currentChecklistDefinitionItems &&
                                    checklistDefinitionItems && (
                                        <Form.Group
                                            className="mb-3"
                                            controlId="checklistItems"
                                        >
                                            <Form.Label>
                                                Checklist Items
                                            </Form.Label>
                                            <EditChecklistDefitionItems
                                                checklistDefinitionItemOptions={
                                                    checklistDefinitionItems
                                                }
                                                onChange={(updatedItems) => {
                                                    setChecklistDefinitionItems(
                                                        updatedItems
                                                    );
                                                }}
                                                onReset={() => {
                                                    setChecklistDefinitionItems(
                                                        currentChecklistDefinitionItems
                                                    );
                                                }}
                                            />
                                            <ErrorMessage
                                                name="type"
                                                component="div"
                                                className="invalid-feedback"
                                            />
                                        </Form.Group>
                                    )}
                                {status && (
                                    <Alert
                                        variant={alertVariant}
                                        className="mt-3"
                                    >
                                        {status}
                                    </Alert>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <div className="form-group">
                                    <Button
                                        variant="primary"
                                        disabled={isSubmitting}
                                        type="submit"
                                        className="me-2"
                                    >
                                        {isSubmitting ? (
                                            <LoadingSpinner text="Updating checklist..." />
                                        ) : (
                                            "Update checklist"
                                        )}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            )}
        </Modal>
    );
};

export { EditChecklist };
